import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Tooltip } from "@mui/material";
import { ArrowDropDownCircleOutlined } from "@mui/icons-material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { toast } from "react-toastify";
import WorkflowRoutePermissionsData from "../utilities/WorkflowRoutePermissionsData";
import { getItem } from "../utilities/UtilityFunctions";
import {
  fetchWorkflowRoutes,
  fetchWorkflowRoutesAssignedPermissions,
  assignPermissionToWorkflow,
  removePermissionFromWorkflow,
} from "../../contents/redux/actions/workflowRouteActions";
import styles from "../utilities/cssmodules/ConfigureWorkflowPermissions.module.css";

const ConfigureWorkflowPermissions = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { workflow, maxAmount } = location.state || {};
  console.log("maxAmount:", maxAmount);
  console.log("Workflow:", workflow);
  const userId = getItem("loggedInUserId");
  const [openDropdowns, setOpenDropdowns] = useState(new Set());
  const [permissionMap, setPermissionMap] = useState({});

  const workflowRoutes = useSelector(
    (state) => state.workflowRoutes.workflowRoutes,
  );
  const assignedPermissions = useSelector(
    (state) => state.workflowRoutes.workflowRoutePermissions,
  );

  // Log workflowRoutes and assignedPermissions for debugging
  useEffect(() => {
    console.log("Workflow Routes:", workflowRoutes);
    console.log("Assigned Permissions:", assignedPermissions);
  }, [workflowRoutes, assignedPermissions]);

  // Redirect if no workflow is provided
  useEffect(() => {
    if (!workflow) {
      navigate("/workflowmanagement");
    }
  }, [workflow, navigate]);

  // Fetch workflow routes and assigned permissions
  useEffect(() => {
    if (workflow?.id) {
      dispatch(fetchWorkflowRoutes(workflow.id));
      dispatch(fetchWorkflowRoutesAssignedPermissions(workflow.id));
    }
  }, [dispatch, workflow]);

  // Go back to previous component
  const goBack = () => {
    navigate(-1);
  };

  // Transform assignedPermissions into a lookup object with roleId and sequence
  useEffect(() => {
    const transformedPermissions = assignedPermissions.reduce((acc, item) => {
      const key = `${item.roleId}_${item.sequence}`;
      if (!acc[key]) {
        acc[key] = new Map();
      }
      acc[key].set(item.workflowPermissionId, item.workflowPermissionId);
      return acc;
    }, {});
    console.log("Transformed Permission Map:", transformedPermissions);
    setPermissionMap(transformedPermissions);
  }, [assignedPermissions]);

  // Toggle dropdown visibility
  const handleDropdownToggle = (routeId) => {
    setOpenDropdowns((prev) => {
      const newDropdowns = new Set(prev);
      if (newDropdowns.has(routeId)) {
        newDropdowns.delete(routeId);
      } else {
        newDropdowns.add(routeId);
      }
      return newDropdowns;
    });
  };

  // Handle permission toggle (assign/remove)
  const handlePermissionToggle = (
    roleId,
    sequence,
    permissionId,
    isChecked,
  ) => {
    if (!workflow?.id || !roleId || !permissionId || !sequence || !userId) {
      console.error("Missing required fields for permission toggle:", {
        workflowId: workflow?.id,
        roleId,
        permissionId,
        sequence,
        userId,
      });
      return;
    }

    const key = `${roleId}_${sequence}`;
    const payload = {
      roleId: roleId,
      workflowId: workflow.id,
      workflowPermissionId: permissionId,
      sequence: sequence,
      userLogId: userId,
    };

    if (isChecked) {
      console.log("Adding permission, payload:", payload);
      dispatch(assignPermissionToWorkflow(payload)).then(() => {
        toast.success("Permission added successfully");
        dispatch(fetchWorkflowRoutesAssignedPermissions(workflow.id));
      });
      setPermissionMap((prev) => {
        const rolePermissions = new Map(prev[key] || []);
        rolePermissions.set(permissionId, permissionId);
        const newMap = { ...prev, [key]: rolePermissions };
        console.log("After adding permission, permissionMap:", newMap);
        return newMap;
      });
    } else {
      const permissionRecord = assignedPermissions.find(
        (p) =>
          p.roleId === roleId &&
          p.sequence === sequence &&
          p.workflowPermissionId === permissionId,
      );
      if (!permissionRecord) {
        console.error("No permission record found for:", {
          roleId,
          sequence,
          permissionId,
        });
        return;
      }
      console.log("Removing permission, permissionRecord:", permissionRecord);
      dispatch(removePermissionFromWorkflow(permissionRecord.id)).then(() => {
        dispatch(fetchWorkflowRoutesAssignedPermissions(workflow.id));
      });
      setPermissionMap((prev) => {
        const rolePermissions = new Map(prev[key] || []);
        rolePermissions.delete(permissionId);
        const newMap = { ...prev, [key]: rolePermissions };
        console.log("After removing permission, permissionMap:", newMap);
        return newMap;
      });
    }
  };

  return (
    <div className="component-wrapper">
      <div className="component-title">
        <div className="flex-row-container">
          <div className="flex-row-custom-1">
            <span className="title-back-button" onClick={goBack}>
              <Tooltip title="Go Back" arrow placement="top">
                <ArrowBackIosIcon />
              </Tooltip>
            </span>
            <span className={styles.workflow_title}>
              Permission Configurations
            </span>
          </div>
        </div>
      </div>
      <div className="wrapper">
        <div className={styles.top_info_text}>
          Configure permissions for <b>{workflow?.name}</b>
        </div>

        <div className={styles.workflow_permission_configuration_wrapper}>
          {workflowRoutes.map((route) => {
            const key = `${route.id}`;
            const permissionKey = `${route.roleId}_${route.sequence}`;
            return (
              <div
                key={key}
                className={styles.workflow_permission_configuration_box}
              >
                <div
                  className={
                    styles.workflow_permission_configuration_box_dropdown_btn
                  }
                  onClick={() => handleDropdownToggle(route.id)}
                  aria-expanded={openDropdowns.has(route.id)}
                  aria-controls={`dropdown-${route.id}`}
                >
                  <span>{route.roleName}</span>
                  <ArrowDropDownCircleOutlined />
                </div>
                {openDropdowns.has(route.id) && (
                  <div
                    id={`dropdown-${route.id}`}
                    className={styles.configuration_box_dropdown}
                  >
                    {WorkflowRoutePermissionsData.map((permission) => {
                      if (
                        permission.permissionName === "Pay" &&
                        maxAmount <= 0
                      ) {
                        return null;
                      }

                      const isChecked =
                        permissionMap[permissionKey]?.has(
                          permission.permissionId,
                        ) || false;
                      console.log(
                        `Rendering checkbox for routeId: ${route.id}, roleId: ${route.roleId}, sequence: ${route.sequence}, permissionId: ${permission.permissionId}, isChecked: ${isChecked}`,
                      );
                      return (
                        <div
                          key={permission.permissionId}
                          className={
                            styles.workflow_permission_configuration_permission_option_box
                          }
                        >
                          <span>{permission.permissionName}</span>
                          <label
                            className={
                              styles.workflow_permission_configuration_permission_option_box_switch
                            }
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={(e) =>
                                handlePermissionToggle(
                                  route.roleId,
                                  route.sequence,
                                  permission.permissionId,
                                  e.target.checked,
                                )
                              }
                              aria-label={`Toggle ${permission.permissionName} permission`}
                            />
                            <span
                              className={
                                styles.workflow_permission_configuration_permission_option_box_switch_slider
                              }
                            ></span>
                          </label>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ConfigureWorkflowPermissions;
